import { useEffect, useState } from "react";
import { Loader2, Trash2, Mail, Phone, Calendar, Users, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Badge } from "../../components/ui/badge";

const STATUS_COLORS = {
  new: "bg-gold/15 text-yellow-700 border-gold/30",
  confirmed: "bg-seafoam-light text-pine border-seafoam",
  done: "bg-gray-100 text-gray-500 border-gray-200",
};

const AdminEnquiries = ({ onChange }) => {
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/enquiries");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Deleted");
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-pine" /></div>;

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-16 text-center" data-testid="enquiries-empty">
        <Inbox className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 font-medium text-gray-500">No enquiries yet</p>
        <p className="text-sm text-gray-400">New tour enquiries and contact messages will appear here, and are also emailed to you.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="enquiries-list">
      {items.map((e) => (
        <div key={e.id} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-soft" data-testid={`enquiry-item-${e.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-pine">{e.name}</p>
                <Badge variant="outline" className={STATUS_COLORS[e.status] || STATUS_COLORS.new} data-testid={`enquiry-status-${e.id}`}>{e.status || "new"}</Badge>
                <Badge variant="outline" className="bg-seafoam-light text-pine border-seafoam/50">
                  {e.type === "contact" ? "Contact message" : e.type === "custom" ? "Custom tour" : "Tour enquiry"}
                </Badge>
              </div>
              {e.tour_name && <p className="mt-0.5 text-sm font-medium text-terracotta">{e.tour_name}</p>}
              <p className="mt-1 text-xs text-gray-400">{new Date(e.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2">
              {e.status === "new" && (
                <button onClick={() => setStatus(e.id, "confirmed")} data-testid={`enquiry-confirm-${e.id}`}
                        className="rounded-full bg-pine px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-pine-light">Mark confirmed</button>
              )}
              {e.status !== "done" && (
                <button onClick={() => setStatus(e.id, "done")} data-testid={`enquiry-done-${e.id}`}
                        className="rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">Mark done</button>
              )}
              <button onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`}
                      className="rounded-full border border-red-100 p-2 text-red-400 hover:bg-red-50" aria-label="Delete enquiry">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-600 sm:grid-cols-2 lg:grid-cols-3">
            <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-seafoam-dark" />{e.email}</span>
            {e.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-seafoam-dark" />{e.phone}</span>}
            {e.date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-seafoam-dark" />{e.date}{e.start_time ? ` · ${e.start_time}` : ""}</span>}
            {(e.adults !== undefined && e.type !== "contact") && (
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-seafoam-dark" />{e.adults} adults{e.children ? `, ${e.children} children` : ""}</span>
            )}
            {e.duration && <span className="text-gray-500">Duration: {e.duration}</span>}
            {e.meeting_point && <span className="text-gray-500">Pickup: {e.meeting_point}</span>}
          </div>
          {(e.special_requests || e.message) && (
            <p className="mt-3 rounded-xl bg-sand p-3 text-sm leading-relaxed text-gray-600">{e.special_requests || e.message}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminEnquiries;
